import { useIntl } from 'react-intl';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { ProductKey } from '@/components/signup/utils/products';
import { CardsGrid } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/cards-grid.tsx';
import { JsmUseCaseCardsGrid } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/jsm-use-case-cards-grid.tsx';
import { ExperimentView } from '@/utils/experiment/experiment-view';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import { useIsJsmCrossflowConfirmation } from '../../hooks/use-is-jsm-crossflow-confirmation';
import { JiraOnboardingContainer } from '../jira/jira-onboarding-container/jira-onboarding-container';
import { JsmUseCaseOnboardingContainer } from '../jsm-use-case-onboarding/jsm-use-case-onboarding-container';
import { ServCoOnboardingContainer } from '../serv-co/serv-co-onboarding-container/serv-co-onboarding-container';
import { TwcOnboardingContainer } from '../twc-provisioning-screen/components/twc-onboarding-container';

type MessagesType = {
	readonly teamFocus: string;
	readonly personalizeJira: string;
};

const TeamFocusView = () => {
	const intl = useIntl();
	const searchParameters = useSearchParameters();
	const { products = [] } = searchParameters;
	const isJsmCrossflow = useIsJsmCrossflowConfirmation();

	const isServCo = products.includes(ProductKey.SERVICE_COLLECTION);
	const isTwc = products.includes(ProductKey.TEAMWORK_COLLECTION);

	const jsmGrowth519Result = useExperiment(experiments.jsmGrowth519JsmLandUseCaseOnboarding);
	const isJsmExperimentEnabled =
		jsmGrowth519Result.type === 'enrolled'
			? jsmGrowth519Result.value
			: experiments.jsmGrowth519JsmLandUseCaseOnboarding.defaultValue;
	const isJsm = products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT);

	// For JSM Land use-case onboarding experiment:
	// enabled (variant) users see JSM-specific card grid (6 upfront + expand).
	// disabled (control) users and non-enrolled users see the original grid.
	const shouldUseJsmUseCaseCards = isJsmExperimentEnabled;
	const cardsGrid = shouldUseJsmUseCaseCards ? <JsmUseCaseCardsGrid /> : <CardsGrid />;
	const shouldUseJsmMessaging = isJsm || shouldUseJsmUseCaseCards;
	const jsmUseCaseSubheader = intl.formatMessage({
		id: 'UserSeg.teamTypeSelection.jsmUseCase.subHeader',
		defaultMessage: "Choose the best fit for your team. We'll help you get started.",
	});

	if (isTwc) {
		return (
			<TwcOnboardingContainer
				header={intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.twc.jtbdD0onboarding.header',
					defaultMessage: 'What kind of work do you do?',
				})}
				subheader={intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.twc.jtbdD0onboarding.subHeader',
					defaultMessage: 'This helps us personalize your experience.',
				})}
			>
				<CardsGrid />
			</TwcOnboardingContainer>
		);
	}

	const messages: MessagesType = shouldUseJsmMessaging
		? {
				teamFocus: intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.header',
					defaultMessage: 'What kind of work do you do?',
				}),
				personalizeJira: shouldUseJsmUseCaseCards
					? jsmUseCaseSubheader
					: intl.formatMessage({
							id: 'UserSeg.teamTypeSelection.jsm.subHeader',
							defaultMessage: 'This helps us personalize your experience',
						}),
			}
		: {
				teamFocus: intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.header',
					defaultMessage: 'What kind of work do you do?',
				}),
				personalizeJira: intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.transparentTemplateForAll.subHeader',
					defaultMessage: 'This helps us suggest templates that help your team do their best work.',
				}),
			};

	const jiraOnboardingContainer = (
		<JiraOnboardingContainer header={messages.teamFocus} subheader={messages.personalizeJira}>
			{cardsGrid}
		</JiraOnboardingContainer>
	);

	if (!isJsm && !isServCo) {
		return jiraOnboardingContainer;
	}

	const servCoMessages: MessagesType = {
		teamFocus: intl.formatMessage({
			id: 'UserSeg.teamTypeSelection.header',
			defaultMessage: 'What kind of work do you do?',
		}),
		personalizeJira: shouldUseJsmUseCaseCards
			? jsmUseCaseSubheader
			: intl.formatMessage({
					id: 'UserSeg.teamTypeSelection.transparentTemplateForAll.subHeader',
					defaultMessage: 'This helps us suggest templates that help your team do their best work.',
				}),
	};

	return (
		<ExperimentView
			experiment={experiments.servCoLofiBackground}
			render={(value) =>
				value && shouldUseJsmUseCaseCards ? (
					<JsmUseCaseOnboardingContainer
						header={servCoMessages.teamFocus}
						subheader={servCoMessages.personalizeJira}
						isJsmCrossflow={isJsmCrossflow}
					>
						{cardsGrid}
					</JsmUseCaseOnboardingContainer>
				) : value ? (
					<ServCoOnboardingContainer
						header={servCoMessages.teamFocus}
						subheader={servCoMessages.personalizeJira}
						isJsmCrossflow={isJsmCrossflow}
					>
						{cardsGrid}
					</ServCoOnboardingContainer>
				) : (
					jiraOnboardingContainer
				)
			}
		/>
	);
};

export default TeamFocusView;
